import { useState } from "react";

import { useRfpClientDetails } from "../../../../hooks/useRfpClientDetails";

import { CoverageDecisionRecord, FinalDecisionStatus } from "./types";
import { useGetCarriers } from "./useGetCarriers";

export const METLIFE_CARRIER_NAME = "MetLife";
export const MIN_EMPLOYEES_FOR_SCA = 500;

/**
 * Determines if the MetLife SCA form modal should be shown.
 * Shows modal if:
 * - Client has 500 or more employees, AND
 * - At least one new coverage (virgin) sold to MetLife, OR
 * - At least one current coverage is moving to MetLife
 */
export const shouldShowMetlifeModal = (
  coverageDecisions: CoverageDecisionRecord[] | undefined,
  carrierMap: Map<number, { id: number; name: string }>,
  numberOfEmployees: number | null | undefined,
): boolean => {
  if (!coverageDecisions || coverageDecisions.length === 0) {
    return false;
  }

  // Check if client has 500 or more employees
  if (numberOfEmployees == null || numberOfEmployees < MIN_EMPLOYEES_FOR_SCA) {
    return false;
  }

  return coverageDecisions.some((decision) => {
    // Check if the winning carrier is MetLife
    const winningCarrier = decision.winningCarrierId
      ? carrierMap.get(decision.winningCarrierId)
      : null;

    if (!winningCarrier || winningCarrier.name !== METLIFE_CARRIER_NAME) {
      return false;
    }

    // New coverage (virgin) sold to MetLife
    if (!decision.isCurrentCoverage) {
      return true;
    }

    // Current coverage moving to MetLife (not renewing with incumbent)
    if (
      decision.isCurrentCoverage &&
      decision.finalDecisionStatus === FinalDecisionStatus.MovingCarriers
    ) {
      return true;
    }

    return false;
  });
};

interface UsePostSubmitNotificationsProps {
  rfpId: string;
}

interface UsePostSubmitNotificationsReturn {
  handlePostSubmit: (coverageDecisions: CoverageDecisionRecord[]) => void;
  showMetlifeModal: boolean;
  hideMetlifeModal: () => void;
}

/**
 * Hook to manage post-submit notifications for final decisions.
 * Currently handles MetLife SCA form notification.
 */
export const usePostSubmitNotifications = ({
  rfpId,
}: UsePostSubmitNotificationsProps): UsePostSubmitNotificationsReturn => {
  const { data: clientDetails } = useRfpClientDetails({ rfpId });
  const { carrierMap } = useGetCarriers(rfpId);

  const [showMetlifeModal, setShowMetlifeModal] = useState(false);

  const handlePostSubmit = (
    coverageDecisions: CoverageDecisionRecord[],
  ): void => {
    const showModal = shouldShowMetlifeModal(
      coverageDecisions,
      carrierMap,
      clientDetails?.numberOfEmployees,
    );

    if (showModal) {
      setShowMetlifeModal(true);
    }
  };

  const hideMetlifeModal = (): void => {
    setShowMetlifeModal(false);
  };

  return {
    handlePostSubmit,
    showMetlifeModal,
    hideMetlifeModal,
  };
};
